import { useEffect, useRef } from 'react'

import { useHandler, useUniqId } from '@surglogs-app/hooks'

interface UseOnDialogBlurProps {
  id?: string
  callback: (event: KeyboardEvent | MouseEvent) => void
  autoRegister?: boolean
}

const STACK: string[] = []

export const useOnDialogBlur = ({
  id: _id,
  callback,
  autoRegister = true,
}: UseOnDialogBlurProps) => {
  const fallbackId = useUniqId()
  const id = _id || fallbackId
  const mouseDownHandlerRef = useRef<(event: MouseEvent) => void>(undefined)

  const handleKeyDown = useHandler((event: KeyboardEvent) => {
    if (event.key === 'Escape' && STACK[STACK.length - 1] === id) {
      callback(event)
    }
  })

  const createMouseDownHandler = useHandler(() => (event: MouseEvent) => {
    const modalElements = document.querySelectorAll(
      '[aria-label="dialog-content"]',
    )
    const currentRef = (modalElements[modalElements.length - 1] ??
      null) as HTMLElement | null

    if (
      currentRef &&
      event.target instanceof Node &&
      !currentRef.contains(event.target) &&
      STACK[STACK.length - 1] === id
    ) {
      callback(event)
    }
  })

  const cleanup = useHandler(() => {
    window.removeEventListener('keydown', handleKeyDown)

    if (mouseDownHandlerRef.current) {
      window.removeEventListener('mousedown', mouseDownHandlerRef.current)
      mouseDownHandlerRef.current = undefined
    }

    const idx = STACK.lastIndexOf(id)

    if (idx !== -1) {
      STACK.splice(idx, 1)
    }
  })

  const register = useHandler(() => {
    STACK.push(id)
    mouseDownHandlerRef.current = createMouseDownHandler()

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('mousedown', mouseDownHandlerRef.current)
  })

  useEffect(() => {
    if (autoRegister) {
      register()
    }

    return () => {
      if (autoRegister) {
        cleanup()
      }
    }
  }, [autoRegister, cleanup, register])

  return { register, cleanup }
}
